import { UseDisclosureReturn } from '@/utils/hooks/useDisclosure';
import useLanguage from '@/utils/hooks/useLanguage';
import { SSOLoadingState, useSso } from '@/utils/providers/SSOProvider';
import cn from 'classnames';
import { Fragment, useMemo } from 'react';
import { IconType } from 'react-icons/lib';
import { LuArrowDownToLine, LuArrowUpFromLine } from 'react-icons/lu';
import { MdCurrencyExchange } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

interface Action {
    label: string;
    Icon: IconType;
    onClick: any;
    show: boolean;
    disabled?: boolean;
    disabledMessage?: string;
    isLoadingSSO?: SSOLoadingState;
    requiresSSO?: boolean;
}

interface LiteActionsProps {
    orderModal: UseDisclosureReturn[1];
    setInitFrom(ccy: string): void;
    setInitTo(ccy: string): void;
}

const LiteActions = ({ orderModal, setInitFrom, setInitTo }: LiteActionsProps) => {
    const lang = useLanguage();
    const navigate = useNavigate();

    const { depositEnabled, withdrawalEnabled, canRequestDeposit, canRequestWithdrawal, isSSOEnabled, isLoadingSSO } =
        useSso();

    const actions: Action[] = useMemo(
        () => [
            {
                label: lang.lite.convert,
                Icon: MdCurrencyExchange,
                onClick: () => {
                    setInitFrom('');
                    setInitTo('');
                    orderModal.toggle();
                },
                show: true
            },
            {
                label: lang.lite.deposit,
                Icon: LuArrowDownToLine,
                onClick: () => navigate('/portal/deposits'),
                show: depositEnabled,
                disabled: !isSSOEnabled || !canRequestDeposit,
                disabledMessage: !canRequestDeposit ? 'Deposit is not available on this user.' : undefined,
                isLoadingSSO,
                requiresSSO: true
            },
            {
                label: lang.lite.withdraw,
                Icon: LuArrowUpFromLine,
                onClick: () => navigate('/portal/withdrawals'),
                show: withdrawalEnabled,
                disabled: !isSSOEnabled || !canRequestWithdrawal,
                disabledMessage: !canRequestWithdrawal ? 'Withdrawal is not available on this user.' : undefined,
                isLoadingSSO,
                requiresSSO: true
            }
        ],
        [orderModal, lang]
    );

    return (
        <Fragment>
            <div className="flex flex-wrap gap-5 py-2 sm:py-4 px-2 sm:p-0">
                {actions.map((action, index) => {
                    const { label, Icon, onClick, show, disabled, disabledMessage } = action;
                    return (
                        <Fragment key={index}>
                            {show && (
                                <div
                                    title={disabledMessage || ''}
                                    className="flex flex-col group items-center gap-1"
                                    onClick={() => {
                                        if (!disabled) onClick();
                                    }}>
                                    <div
                                        className={cn('flex items-center justify-center p-2 sm:p-3 rounded-full', {
                                            ['text-brand-module-title bg-brand-accent group-hover:bg-brand-accent-light cursor-pointer']:
                                                !disabled,
                                            ['bg-brand-neutral-700 group-hover:bg-brand-neutral-600 cursor-not-allowed']:
                                                disabled
                                        })}>
                                        <Icon className="h-3 w-3 sm:h-4 sm:w-4" />
                                    </div>
                                    <span className="text-xs sm:text-sm">{label}</span>
                                </div>
                            )}
                        </Fragment>
                    );
                })}
            </div>
        </Fragment>
    );
};

export default LiteActions;
